import Spinner from "@/components/atoms/Spinner/Spinner";
import { RewardBand } from "@/components/organisms/SBCs/RewardBand";
import { SetHeaderDetails } from "@/components/organisms/SBCs/SbcSetHeader/SetHeaderDetails";
import { SetRewardHeader } from "@/components/organisms/SBCs/SbcSetHeader/SetRewardHeader";
import { SetProgress } from "@/components/organisms/SBCs/SetProgress";
import { useSeoCopy } from "@/config/seo";
import { HeroSection } from "@/containers/HeroSection";
import Card from "@/design-system/Card/Card";
import { extractRewardUrl } from "@/functions/extractRewardUrl";
import { useGoBack } from "@/hooks/useGoBack";
import { cn } from "@/lib/utils";
import {
  fetchLatestObjectiveBundle,
  fetchObjectiveGroup,
  getObjectivesSelector,
} from "@/redux/objectives/objectivesSlice";
import { AppDispatch } from "@/redux/store";
import { userIsLoggedInSelector } from "@/redux/user/userSlice";
import type { ObjectiveChallenge } from "@repo/shared/api";
import { useTranslation } from "@repo/shared/i18n";
import { objectiveReward } from "@repo/shared/utils";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router";

import { AddToBundleButton } from "./AddToBundleButton";

const TAG = "flex h-5 items-center rounded-full px-2 text-xs";

const KINDS = [
  "match",
  "sbc",
  "market",
  "club",
  "rush",
  "moments",
  "objective",
  "placement",
  "watch",
  "other",
] as const;

const ChallengeCard = ({ challenge }: { challenge: ObjectiveChallenge }) => {
  const { t } = useTranslation("objectives");
  const kind = KINDS.find((k) => k === challenge.kind);
  // No status means the parser never produced a requirement.
  const parseMarker =
    challenge.status === "partial"
      ? t("parse.partial")
      : challenge.status === "unresolvable"
        ? t("parse.unresolvable")
        : !challenge.status
          ? t("parse.unparsed")
          : undefined;

  return (
    <Card
      hoverable={false}
      className={cn(
        "flex h-full flex-col overflow-hidden",
        challenge.completed && "opacity-50",
      )}
    >
      <div className="flex flex-col gap-y-2 px-5 pb-4 pt-5">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-base font-semibold leading-tight text-foreground">
            {challenge.name}
          </h3>
          {(kind || challenge.kind) && (
            <span className={cn(TAG, "bg-indigo-900/50 text-indigo-400")}>
              {kind ? t(`kind.${kind}`) : challenge.kind}
            </span>
          )}
          {parseMarker && (
            <span className={cn(TAG, "bg-amber-900/50 text-amber-400")}>
              {parseMarker}
            </span>
          )}
        </div>
        <p className="text-sm text-foreground-body">{challenge.description}</p>
      </div>
      {/* Signed in: the group card's progress row. */}
      {challenge.target !== undefined && (
        <SetProgress
          className="px-5 pb-4"
          solvedCount={challenge.progress ?? 0}
          sbcsCount={challenge.target}
        />
      )}
      <div className="flex-1" />
      {challenge.rewards.map((raw, index) => {
        const reward = objectiveReward(raw, t);
        return (
          <RewardBand
            key={index}
            reward={reward}
            rewardUrl={extractRewardUrl(reward)}
          />
        );
      })}
    </Card>
  );
};

// Modelled on the SBC set page: group header, then one card per challenge.
const ObjectiveGroupPage = () => {
  const { t } = useTranslation("objectives");
  const objectivesSeo = useSeoCopy("objectives");
  const { groupId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { currentGroup } = useSelector(getObjectivesSelector);
  const isLoggedIn = useSelector(userIsLoggedInSelector);
  const group =
    currentGroup.data?.id === Number(groupId) ? currentGroup.data : undefined;
  const goBack = useGoBack({ fallback: "/objectives" });

  // Unknown or inactive ids 404; back to the list.
  useEffect(() => {
    dispatch(fetchObjectiveGroup(Number(groupId)))
      .unwrap()
      .catch(() => navigate("/objectives"));
  }, [dispatch, groupId, navigate]);

  // Adding a group before the saved bundle is known would overwrite it.
  useEffect(() => {
    if (isLoggedIn) dispatch(fetchLatestObjectiveBundle());
  }, [dispatch, isLoggedIn]);

  const reward = objectiveReward(group?.rewards[0], t);
  const required = group?.nObjectivesRequired ?? group?.nObjectives ?? 0;

  return (
    <main className="min-h-screen bg-background">
      <HeroSection
        helmetTitle={group?.name ?? objectivesSeo.title}
        back={goBack}
        shouldBeInTheDocumentFlow
        hidePageDescription
      />
      <div className="flex w-full flex-col">
        {group ? (
          <>
            <div className="mx-auto flex w-full max-w-content flex-wrap items-center gap-8 px-4 py-4 desktop:mt-2 large:px-16">
              <div className="flex min-w-0 flex-1 flex-col gap-y-2">
                <h1 className="text-base font-bold leading-tight text-foreground desktop:text-2xl">
                  {group.name}
                </h1>
                <p className="text-sm text-foreground-body">
                  {group.description}
                </p>
                {group.completedCount !== undefined && (
                  <SetProgress
                    className="max-w-80"
                    solvedCount={Math.min(group.completedCount, required)}
                    sbcsCount={required}
                  />
                )}
                <SetHeaderDetails
                  repeatable={
                    !!group.repeatabilityMode &&
                    group.repeatabilityMode !== "NON_REPEATABLE"
                  }
                  repeats={group.repeats}
                  refreshInterval={group.refreshInterval}
                  startTime={group.startTime ?? 0}
                  endTime={group.endTime}
                />
                <div>
                  <AddToBundleButton
                    groupId={group.id}
                    matchModes={group.matchModes}
                    liveEvents={group.liveEvents}
                    completed={group.completed}
                    locked={group.locked}
                  />
                </div>
              </div>
              {reward && (
                <div className="flex min-w-50 flex-1 items-center">
                  <SetRewardHeader
                    reward={reward}
                    setName={group.name}
                    setDescription={group.description}
                  />
                </div>
              )}
            </div>
            <div className="mx-auto w-full max-w-content p-4 large:px-16">
              {group.challenges.length > 0 ? (
                <div className="grid grid-cols-1 gap-4 desktop:grid-cols-2 touch-device:grid-cols-3">
                  {group.challenges.map((challenge) => (
                    <ChallengeCard key={challenge.id} challenge={challenge} />
                  ))}
                </div>
              ) : (
                <p className="text-center text-sm text-foreground-muted">
                  {t("group.empty")}
                </p>
              )}
            </div>
          </>
        ) : (
          <div className="m-auto">
            <Spinner />
          </div>
        )}
      </div>
    </main>
  );
};

export default ObjectiveGroupPage;
